import React, { useRef } from "react";
import { useHotkeys } from "react-hotkeys-hook";

import { useSuppressBrowserAutocomplete } from "../forms/suppress-browser-autocomplete";
import "./list-header.css";

export interface ListHeaderProps {
  filter?: string;
  onFilterChange?: (value: string) => void;
  onPrintRequested?: () => void;
  /** Rendered after the search field and before the print button (e.g. sort menu). */
  beforePrint?: React.ReactNode;
}

export const ListHeader: React.FC<React.PropsWithChildren<ListHeaderProps>> = ({
  filter,
  children,
  onFilterChange,
  onPrintRequested,
  beforePrint
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const browserAutocompleteSuppression = useSuppressBrowserAutocomplete(undefined);
  useHotkeys("ctrl+f, meta+f", e => {
    e.preventDefault();
    inputRef.current && inputRef.current.focus();
  });

  return (
    <div className="d-flex align-items-center w-100 mb-2">
      <div className="form-floating has-icon flex-grow-1">
        <input
          type="text"
          ref={inputRef}
          className="form-control"
          id="contentSearch"
          placeholder="Search"
          value={filter}
          readOnly={browserAutocompleteSuppression.readOnly}
          onFocus={browserAutocompleteSuppression.onFocus}
          autoComplete={browserAutocompleteSuppression.autoComplete}
          autoCorrect={browserAutocompleteSuppression.autoCorrect}
          spellCheck={browserAutocompleteSuppression.spellCheck}
          data-1p-ignore={browserAutocompleteSuppression["data-1p-ignore"]}
          data-lpignore={browserAutocompleteSuppression["data-lpignore"]}
          onChange={e => {
            onFilterChange && onFilterChange(e.target.value);
          }}
        />
        <label htmlFor="contentSearch">Search</label>
        <span className="fas fa-search" aria-hidden="true"></span>
      </div>

      {beforePrint ? <div className="list-header-before-print">{beforePrint}</div> : null}

      <button type="button" className="btn btn-sm list-header-toolbar-action" onClick={onPrintRequested}>
        <span className="fas fa-print" aria-hidden="true" />
        <span className="sr-only">Print</span>
      </button>
      {children}
    </div>
  );
};
